/**
 * WorkBuddy 会话的跨窗口登录态同步。
 *
 * localStorage 是多窗口共享的，但每个窗口的 Vuex 状态与已渲染界面各自独立：在一个窗口解除
 * 绑定并退出登录后，其余窗口的界面仍停在旧账号的登录态上，直到用户点某个操作才会因缺少
 * token 而失败。本模块借 storage 事件感知「登录态被其它窗口清掉」，随后交给
 * workBuddySessionRecovery 静默重建登录态，用户无需重新登录。
 *
 * 只接管 `openFrom=workbuddy` 会话，普通浏览器多窗口维持原有行为。
 */
import { checkLogin } from '@/utils/storageOperateApi';
import { isWorkBuddySessionRecovering, startWorkBuddySessionRecovery } from '@/utils/workBuddySessionRecovery';
import slsLogger from '@/utils/slsLogger';

/** slsLogger 组件名 */
const LOG_COMPONENT = 'WorkBuddySessionSync';

/** 本窗口是否见过登录态：没见过说明它一直停在登录/授权页，不应被别处的退出登录打断 */
let hasSeenLoginState = false;

/**
 * 处理其它窗口写入 storage 的变更，识别出登录态被清除后启动静默恢复。
 * @param {import('vue-router').Router} router - 应用路由实例
 * @returns {void}
 */
function handleStorageChange(router) {
  const loginStateExists = checkLogin();

  // 登录态仍在，本次变更与退出登录无关；同时重新打开「已登录」边沿，允许后续一次失效恢复。
  if (loginStateExists) {
    hasSeenLoginState = true;
    return;
  }
  // 本窗口从未持有登录态（例如正停在登录页输验证码），不该被别处的退出登录打断
  if (!hasSeenLoginState) return;

  // storage.clear() / 逐项写回会产生多个 storage 事件，只消费第一次「已登录 → 未登录」边沿。
  hasSeenLoginState = false;

  // 已在恢复中：后续清理和写回事件无需重复启动恢复。
  if (isWorkBuddySessionRecovering()) return;

  // 只有恢复链路真正接管时才上报，避免非 WorkBuddy、路由尚未就绪或冷却期内刷屏。
  if (!startWorkBuddySessionRecovery()) return;

  slsLogger.console({
    component: LOG_COMPONENT,
    message: 'WorkBuddy 跨窗口同步-检测到登录态被其它窗口清除',
    extra: { path: router.currentRoute.value.fullPath }
  });
}

/**
 * 安装跨窗口登录态同步，需在应用启动时调用一次。
 * @param {import('vue-router').Router} router - 应用路由实例
 * @returns {void}
 * @example
 * installWorkBuddySessionSync(router);
 */
export function installWorkBuddySessionSync(router) {
  hasSeenLoginState = checkLogin();

  // 本窗口自己登录成功不会触发 storage 事件，借导航补记一次登录态
  router.afterEach(() => {
    if (checkLogin()) hasSeenLoginState = true;
  });

  // storage 事件只在其它窗口修改时触发，正好用来感知别处的退出登录
  window.addEventListener('storage', () => handleStorageChange(router));
}
